import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import CourseDetailsModal from "./courseDetailsModal";
import PreLoader from "../../../core/common/preLoader";
import { Course } from "../../../core/data/interface";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders } from "../../../common/Utils";
import "./courses.css";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";

// Helper function to count study materials
const countStudyMaterials = (course: Course) => {
  let videos = 0;
  let documents = 0;
  let links = 0;
  
  course.courseDetails?.subjects?.forEach(subject => {
    subject.syllabus.forEach(syllabus => {
      syllabus.studyMaterials.forEach(material => {
        switch (material.materialType) {
          case 'VIDEO':
            videos++;
            break;
          case 'NOTE':
          case 'FILE':
            documents++;
            break;
          case 'LINK':
            // Check if it's a YouTube link (including YouTube Shorts)
            if (material.content && (material.content.includes('youtube.com') || material.content.includes('youtu.be') || material.content.includes('m.youtube.com'))) {
              videos++;
            } else {
              links++;
            }
            break;
        }
      });
    });
  });
  
  return { videos, documents, links };
};

// Helper function to get course badge
const getCourseBadge = (course: Course, collections: any) => {
  // Priority: Enrolled > Popular > Trending > Most Viewed
  if (collections.enrolled_courses.some((c: Course) => c.courseId === course.courseId)) {
    return { text: 'Enrolled', color: 'bg-purple' };
  }
  if (collections.popular_courses.some((c: Course) => c.courseId === course.courseId)) {
    return { text: 'Popular', color: 'bg-danger' };
  }
  if (collections.trending_courses.some((c: Course) => c.courseId === course.courseId)) {
    return { text: 'Trending', color: 'bg-success' };
  }
  if (collections.most_viewed.some((c: Course) => c.courseId === course.courseId)) {
    return { text: 'Most Viewed', color: 'bg-primary' };
  }
  return null;
};

// Helper function to calculate discount percentage
const calculateDiscountPercentage = (originalPrice: number, promotionalPrice: number) => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - promotionalPrice) / originalPrice) * 100);
};


const Courses = () => {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["All"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [data, setData] = useState<any>(null);
  
  // API call setup
  const { isLoading, error, data: apiData, status, callFetch } = useFetch();

  const fetchCourseData = useCallback(() => {
    callFetch(
      API.COURSE_HOME_DATA,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).catch((err) => {
      console.error('Failed to fetch course data:', err);
    });
  }, []);

  // Fetch course data on component mount
  useEffect(() => {
    fetchCourseData();
  }, []);

  // Handle API response
  useEffect(() => {
    if (apiData && status && status < 299) {
      setData(apiData);
    }
  }, [apiData, status]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      console.error('Error fetching course data:', error);
    }
  }, [error]);

  // Get unique course types from API data
  const getAllCourseTypes = () => {
    if (!data?.courseCollections) return ["All"];
    const allCourses = [
      ...(data.courseCollections.available_courses || []),
      ...(data.courseCollections.enrolled_courses || [])
    ];
    const uniqueTypes = Array.from(new Set(allCourses.map(course => course.courseType)));
    return ["All", ...uniqueTypes];
  };

  // Handle subscribe button click
  const handleSubscribe = (course: Course) => {
    const activeSubscription = course.availableSubscriptions?.find(sub => sub.active);
    
    if (!activeSubscription) {
      console.error('No active subscription found for this course');
      return;
    }

    // Create payment details object
    const paymentDetails = {
      courseId: course.courseId,
      courseTitle: course.courseTitle,
      courseDescription: course.courseDescription,
      courseThumbnail: course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL,
      subscriptionPlan: {
        subscriptionPlanId: activeSubscription.subscriptionPlanId,
        planName: activeSubscription.planName,
        duration: activeSubscription.duration,
        price: activeSubscription.pricing.price,
        promotionalPrice: activeSubscription.pricing.promotionalPrice,
        currencyCode: activeSubscription.pricing.currencyCode
      },
      totalAmount: activeSubscription.pricing.price,
      discountAmount: activeSubscription.pricing.price - activeSubscription.pricing.promotionalPrice,
      finalAmount: activeSubscription.pricing.promotionalPrice
    };

    // Navigate to payment page with course details
    navigate(all_routes.order, {
      state: { paymentDetails }
    });
  };

  const categories = getAllCourseTypes();

  // Handle chip selection
  const handleChipClick = (category: string) => {
    if (category === "All") {
      setSelectedCategories(["All"]);
    } else {
      setSelectedCategories(prev => {
        // Remove "All" if it's selected
        const newCategories = prev.filter(cat => cat !== "All");
        
        // If category is already selected, remove it; otherwise add it
        if (newCategories.includes(category)) {
          const updated = newCategories.filter(cat => cat !== category);
          // If no categories left, select "All"
          return updated.length === 0 ? ["All"] : updated;
        } else {
          return [...newCategories, category];
        }
      });
    }
  };

  // Get enrolled and available courses
  const enrolledCourses = data?.courseCollections?.enrolled_courses || [];
  const availableCourses = data?.courseCollections?.available_courses || [];
  
  // Show message if no data is available
  if (!data && !isLoading && !error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-book-off display-1 text-muted mb-3"></i>
              <h4>No courses available</h4>
              <p className="text-muted">There are no courses to display at the moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filterCourses = (courses: Course[]) => {
    return courses.filter(course => {
      // Filter by selected categories
      const categoryMatch = selectedCategories.includes("All") || selectedCategories.includes(course.courseType);
      
      // Filter by search term
      const searchMatch = searchTerm === "" || 
        course.courseTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.courseDescription.toLowerCase().includes(searchTerm.toLowerCase());
      
      return categoryMatch && searchMatch;
    });
  };

  const filteredEnrolledCourses = filterCourses(enrolledCourses);
  const filteredAvailableCourses = filterCourses(availableCourses);

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star text-warning"></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
    }

    return stars;
  };

  const renderCourseCard = (course: Course, isEnrolled: boolean = false) => {
    const materials = countStudyMaterials(course);
    const badge = getCourseBadge(course, data.courseCollections);
    const activeSubscription = course.availableSubscriptions?.find(sub => sub.active);
    const discountPercentage = activeSubscription ? 
      calculateDiscountPercentage(activeSubscription.pricing.price, activeSubscription.pricing.promotionalPrice) : 0;
    
    return (
      <div key={course.courseId} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
        <div className="card course-card h-100">
          {/* Course Thumbnail */}
          <div className="position-relative">
            <ImageWithBasePath
              src={course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
              alt={course.courseTitle}
              className="card-img-top course-thumbnail"
            />
            {/* Badges */}
            <div className="position-absolute top-0 start-0 p-2">
              {badge && (
                <span className={`badge ${badge.color}`}>{badge.text}</span>
              )}
            </div>
            <div className="position-absolute top-0 end-0 p-2">
              <span className="badge bg-secondary">{course.courseType}</span>
            </div>
            {course.courseMetadata?.difficulty_level && (
              <div className="position-absolute bottom-0 end-0 p-2">
                <span className="badge bg-info">{course.courseMetadata?.difficulty_level}</span>
              </div>
            )}
          </div>

          <div className="card-body">
            {/* Course Info */}
            <div className="mb-1">
              <h5 className="card-title mb-2">{course.courseTitle}</h5>
              <p className="card-text text-muted small mb-2" style={{ 
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>{course.courseDescription}</p>
              
              {/* Rating and Analytics */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <div className="me-1">
                    {renderStars(course.analytics.averageRating)}
                  </div>
                  <span className="small fw-medium">{course.analytics.averageRating.toFixed(1)}</span>
                  <span className="small text-muted ms-1">({course.analytics.totalReviews} reviews)</span>
                </div>
              </div>

              {/* Study Materials Count */}
              <div className="d-flex justify-content-between small text-muted mb-2">
                <span>{materials.videos} Videos</span>
                <span>{materials.documents + materials.links} Study Materials</span>
              </div>

              {/* Progress Bar for Enrolled Courses */}
              {isEnrolled && course.courseAccessInfo && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Progress</span>
                    <span>{course.courseAccessInfo.progressPercentage}%</span>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div 
                      className="progress-bar" 
                      style={{width: `${course.courseAccessInfo.progressPercentage}%`}}
                    ></div>
                  </div>
                  {course.courseAccessInfo.progressPercentage === 0 && course.isStarted && (
                    <small className="text-info">Course Started</small>
                  )}
                </div>
              )}
            </div>

            {/* Subscription Plan */}
            {activeSubscription && (
              <div className="mb-3">
                <div className="p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <div className="small text-muted">{activeSubscription.duration ? `${activeSubscription.duration} month(s)` : 'Lifetime access'}</div>
                    </div>
                    <div className="text-end">
                      <div className="d-flex align-items-center">
                        {activeSubscription.pricing.price > 0 && (
                          <span className="text-decoration-line-through text-muted me-2 small">
                            {activeSubscription.pricing.currencyCode} {activeSubscription.pricing.price}
                          </span>
                        )}
                        <span className="h5 fw-bold text-primary mb-0">
                          {activeSubscription.pricing.currencyCode} {activeSubscription.pricing.promotionalPrice}
                        </span>
                      </div>
                      {discountPercentage > 0 && (
                        <div className="small text-success">
                          {discountPercentage}% OFF
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons - Fixed at bottom */}
            <div className="card-buttons d-grid gap-2">
              {!isEnrolled ? (
                <>
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleSubscribe(course)}
                  >
                    <i className="ti ti-shopping-cart me-2"></i>
                    Enroll Now
                  </button>
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => setSelectedCourse({...course, isEnrolled: false})}
                  >
                    <i className="ti ti-eye me-2"></i>
                    View Details
                  </button>
                </>
              ) : (
                <button 
                  className="btn btn-outline-primary"
                  onClick={() => setSelectedCourse({...course, isEnrolled: true})}
                >
                  <i className="ti ti-eye me-2"></i>
                  View Details
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state
  if (isLoading) {
    return (
      <>
        <PreLoader showLoader={true} />
        <div className="page-wrapper">
          <div className="content">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <p className="text-muted">Loading courses...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
              <h4>Failed to load courses</h4>
              <p className="text-muted">Please try again later.</p>
              <button className="btn btn-primary" onClick={fetchCourseData}>
                <i className="ti ti-refresh me-2"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Courses</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Courses
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Search and Filters */}
          <div className="card mb-3">
            <div className="card-body py-3">
              {/* Search Bar */}
              <div className="mb-3">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search courses by title or description..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button className="btn btn-primary" type="button">
                    <i className="ti ti-search"></i>
                  </button>
                </div>
              </div>

              {/* Category Chips */}
              <div>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      className={`btn badge ${
                        selectedCategories.includes(category) 
                          ? 'bg-primary' 
                          : 'bg-secondary'
                      } border-0 px-3 py-1`}
                      onClick={() => handleChipClick(category)}
                      style={{ cursor: 'pointer' }}
                    >
                      {category === 'All' ? 'All' : category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>



          {/* Available Courses Section */}
          <div className="mb-4">
            <h4 className="mb-3">
              <i className="ti ti-book-open me-2 text-success"></i>
              Available Courses
            </h4>
          </div>
          <div className="row">
            {filteredAvailableCourses.map((course) => renderCourseCard(course, false))}
          </div>

                    {/* Enrolled Courses Section */}
          {filteredEnrolledCourses.length > 0 && (
            <>
              <div className="mb-4">
                <h4 className="mb-3">
                  <i className="ti ti-bookmark-filled me-2 text-primary"></i>
                  My Enrolled Courses
                </h4>
              </div>
              <div className="row mb-5">
                {filteredEnrolledCourses.map((course) => renderCourseCard(course, true))}
              </div>
            </>
          )}

          {/* No Results */}
          {filteredEnrolledCourses.length === 0 && filteredAvailableCourses.length === 0 && (
            <div className="text-center py-5">
              <i className="ti ti-search-off display-1 text-muted"></i>
              <h4 className="mt-3">No courses found</h4>
              <p className="text-muted">Try adjusting your filters to find more courses.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
         <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Course Details Modal */}
      <CourseDetailsModal 
        course={selectedCourse} 
        onClose={() => setSelectedCourse(null)} 
      />
    </>
  );
};

export default Courses;